import React, { useState, useEffect } from 'react';
import { Play, Activity, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const APIToolsTab = () => {
  const [health, setHealth] = useState({ status: 'checking', timestamp: null });
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const apiEndpoints = [
    {
      category: 'Authentication',
      endpoints: [
        { method: 'GET', path: '/auth/me', name: 'Get Current User', auth: true },
        { method: 'POST', path: '/auth/login', name: 'Login', auth: false },
        { method: 'POST', path: '/auth/register', name: 'Register', auth: false },
      ],
    },
    {
      category: 'Users',
      endpoints: [
        { method: 'GET', path: '/users', name: 'Get All Users', auth: true },
        { method: 'GET', path: '/users/stats/summary', name: 'Get User Stats', auth: true },
      ],
    },
    {
      category: 'Items',
      endpoints: [
        { method: 'GET', path: '/baby-items', name: 'Get All Items', auth: false },
        { method: 'GET', path: '/baby-items/stats/summary', name: 'Get Item Stats', auth: true },
        { method: 'GET', path: '/baby-items/categories', name: 'Get Categories', auth: false },
      ],
    },
    {
      category: 'Transactions',
      endpoints: [
        { method: 'GET', path: '/transactions', name: 'Get Transactions', auth: true },
        { method: 'GET', path: '/transactions/admin/all', name: 'Get All Transactions (Admin)', auth: true },
        { method: 'GET', path: '/transactions/stats/summary', name: 'Get Transaction Stats', auth: true },
      ],
    },
    {
      category: 'Themes',
      endpoints: [
        { method: 'GET', path: '/themes', name: 'Get All Themes', auth: false },
        { method: 'GET', path: '/themes/current', name: 'Get Current Theme', auth: false },
      ],
    },
    {
      category: 'Admin',
      endpoints: [
        { method: 'GET', path: '/admin/dashboard/stats', name: 'Get Dashboard Stats', auth: true },
        { method: 'GET', path: '/admin/dashboard/activity', name: 'Get Recent Activity', auth: true },
        { method: 'GET', path: '/admin/settings', name: 'Get Settings', auth: true },
      ],
    },
  ];

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  const checkHealth = async () => {
    try {
      const start = Date.now();
      await api.get('/health');
      const responseTime = Date.now() - start;

      setHealth({
        status: 'online',
        responseTime,
        timestamp: new Date(),
      });
    } catch (err) {
      setHealth({
        status: 'offline',
        timestamp: new Date(),
      });
    }
  };

  const testEndpoint = async (endpoint) => {
    setLoading(true);
    setTestResult(null);

    try {
      const start = Date.now();
      let response;

      if (endpoint.method === 'GET') {
        response = await api.get(endpoint.path);
      } else if (endpoint.method === 'POST') {
        response = await api.post(endpoint.path, {});
      }

      const responseTime = Date.now() - start;

      setTestResult({
        success: true,
        status: response.status,
        responseTime,
        data: response.data,
      });
    } catch (err) {
      setTestResult({
        success: false,
        status: err.response?.status || 'Error',
        error: err.response?.data?.error || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>API Tools</h1>

      {/* Health Status */}
      <div style={{
        background: 'var(--cardBackground)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '32px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ margin: 0 }}>API Health Status</h2>
          <button
            onClick={checkHealth}
            style={{
              padding: '8px 16px',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '8px' }}>Status</div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '18px',
              fontWeight: '600',
              color: health.status === 'online' ? 'var(--success)' : health.status === 'offline' ? 'var(--error)' : 'var(--warning)',
            }}>
              <Activity size={20} />
              {health.status}
            </div>
          </div>

          {health.responseTime && (
            <div>
              <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '8px' }}>Response Time</div>
              <div style={{ fontSize: '18px', fontWeight: '600' }}>{health.responseTime}ms</div>
            </div>
          )}

          {health.timestamp && (
            <div>
              <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '8px' }}>Last Checked</div>
              <div style={{ fontSize: '18px', fontWeight: '600' }}>{health.timestamp.toLocaleTimeString()}</div>
            </div>
          )}
        </div>
      </div>

      {/* API Endpoints */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Endpoints List */}
        <div>
          <h2>API Endpoints</h2>
          {apiEndpoints.map((category) => (
            <div key={category.category} style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', color: 'var(--textSecondary)', marginBottom: '12px' }}>
                {category.category}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {category.endpoints.map((endpoint, index) => (
                  <div
                    key={index}
                    style={{
                      background: 'var(--cardBackground)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      padding: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          background: endpoint.method === 'GET' ? 'var(--success)' : 'var(--accent)',
                          color: 'white',
                        }}>
                          {endpoint.method}
                        </span>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{endpoint.name}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--textSecondary)', fontFamily: 'monospace' }}>
                        {endpoint.path}
                      </div>
                    </div>
                    <button
                      onClick={() => testEndpoint(endpoint)}
                      disabled={loading}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px',
                      }}
                    >
                      <Play size={14} />
                      Test
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Test Result */}
        <div>
          <h2>Test Result</h2>
          {!testResult && !loading && (
            <div style={{
              background: 'var(--cardBackground)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
              color: 'var(--textSecondary)',
            }}>
              Select an endpoint and click "Test" to see the response
            </div>
          )}

          {loading && (
            <div style={{
              background: 'var(--cardBackground)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '40px',
              textAlign: 'center',
            }}>
              <RefreshCw size={40} className="spin" style={{ color: 'var(--primary)' }} />
              <p style={{ marginTop: '16px', color: 'var(--textSecondary)' }}>Testing endpoint...</p>
            </div>
          )}

          {testResult && (
            <div style={{
              background: 'var(--cardBackground)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '20px',
            }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '8px' }}>Status</div>
                <div style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: testResult.success ? 'var(--success)' : 'var(--error)',
                  color: 'white',
                }}>
                  {testResult.status}
                </div>
              </div>

              {testResult.responseTime && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '8px' }}>Response Time</div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>{testResult.responseTime}ms</div>
                </div>
              )}

              <div>
                <div style={{ fontSize: '14px', color: 'var(--textSecondary)', marginBottom: '8px' }}>Response</div>
                <pre style={{
                  background: 'var(--background)',
                  padding: '16px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '400px',
                  fontFamily: 'monospace',
                }}>
                  {JSON.stringify(testResult.data || testResult.error, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default APIToolsTab;
